import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageV2DTO } from 'support/types';
import { Translate } from 'components/Translate/Translate';

type AlertConfig = {
  severity: 'warning' | 'info';
  title: string;
  description: JSX.Element;
};

export const useAlertConfig = (review: MessageV2DTO['review'], message: MessageV2DTO): AlertConfig => {
  const { t } = useTranslation();

  const alertConfig = useMemo(() => {
    switch (review?.type) {
      case 'missing_document':
        return {
          severity: 'warning' as const,
          title: t('transactions:show.sections.alert.review.missingDocument.title', {
            documentType: t(`common:messageTypes.singular.${message.type}`),
          }),
          description: <Translate i18nKey="transactions:show.sections.alert.review.missingDocument.description" />,
        };
      case 'document_changed':
        return {
          severity: 'info' as const,
          title: t('transactions:show.sections.alert.review.documentChanged.title'),
          description: <Translate i18nKey="transactions:show.sections.alert.review.documentChanged.description" />,
        };
      case 'document_requires_manual_review':
        return {
          severity: 'info' as const,
          title: t('transactions:show.sections.alert.review.manualReview.title'),
          description: <Translate i18nKey="transactions:show.sections.alert.review.manualReview.description" />,
        };
      case 'document_requires_manual_delivery':
      default:
        return {
          severity: 'info' as const,
          title: t('transactions:show.sections.alert.review.title', {
            documentType: t(`common:messageTypes.plural.${message.type}`),
          }),
          description: <Translate i18nKey="transactions:show.sections.alert.review.description" />,
        };
    }
  }, [review, message, t]);

  return alertConfig;
};
